import { Badge, Dropdown, Flex, Space, Tag, Tooltip, Typography } from "antd"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

import ButtonComponent from "../../../../Components/Button"
import Private, { INCOGNITO_MASK } from "../../../../Components/Private"
import { titleSortFilters } from "../../../../Components/titleSortFilters"
import Icon from "../../../../Icon"
import Images from "../../../../Images"
import { isIncognitoActive } from "../../../../Utils/incognito"
import {
  alertsColumnIconMap,
  alertsTooltipStatusText,
  creditsColumnIconMap,
  dateFormat,
  paymentProcessorMap,
  pinIconColor,
  remainingTime,
  serviceImg,
  serviceMap,
  statusTextMap,
  tagColorMap,
  tagMap
} from "../../../../Utils/utils"

dayjs.extend(utc)

const { Text, Link } = Typography

const dataCell = (_) => (
  <Space direction="vertical" size={4}>
    <Text>{dayjs(_).format(dateFormat)}</Text>
    <Text type="secondary">{dayjs(_).format("h:mm A [(UTC)]")}</Text>
  </Space>
)

export const availableRequestStatusesMap = {
  pending: ["requires proof", "rejected", "approved"],
  "requires proof": ["rejected", "approved"],
  reopened: ["rejected", "approved"],
  rejected: ["approved"]
}

export const availableProviderStatusesMap = {
  pending: ["request sent", "requires proof", "rejected", "approved", "ineligible"],
  "requires proof": ["rejected", "approved", "request sent"],
  "request sent": ["requires proof", "rejected", "approved", "ineligible"],
  rejected: ["approved", "request sent"]
}

export const getColumns = ({
  t,
  options,
  handleChangeStatus,
  theme,
  isAdmin,
  onSelectRow,
  disabledStatus = false,
  strong = false
}) => ({
  icon: {
    key: "pinned",
    title: "",
    dataIndex: "pinned",
    width: 64,
    fixed: "left",
    render: (_, record) => {
      if (record?.["request_status"]?.toLowerCase() === "requires proof") {
        return (
          <Icon
            type={"clock_alert"}
            style={{ fontSize: "20px", margin: "0 6px", color: "#D71313" }}
          />
        )
      } else {
        return (
          <Tooltip title={t(_ ? "Unpin" : "Pin")}>
            <ButtonComponent
              type="text"
              shape="circle"
              icon={<Icon color={pinIconColor[theme]} type={_ ? "pin_active" : "pin"} />}
              onClick={(e) => {
                e.stopPropagation()
                onSelectRow(record, "pin")
              }}
              className="flex center"
              style={{ padding: 0 }}
            />
          </Tooltip>
        )
      }
    }
  },
  id: {
    key: "sequence_number",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: null,
        sorter: true,
        icon: alertsColumnIconMap["id"],
        stateData: state,
        key: "sequence_number"
      }),
    dataIndex: "sequence_number",
    width: 100,
    fixed: "left"
  },
  request_status: {
    key: "request_status",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: t("Request status"),
        sorter: true,
        icon: creditsColumnIconMap["request_status"],
        stateData: state,
        key: "request_status"
      }),
    dataIndex: "request_status",
    fixed: isAdmin ? false : "left",
    minWidth: 180,
    render: (_, record) => {
      let tagType = { ...tagMap, pending: "purple" }[_?.toLowerCase()]
      const time =
        _?.toLowerCase() === "requires proof" && record?.["proof_deadline"]
          ? remainingTime(null, null, record?.["proof_deadline"])
          : null
      const tooltip =
        _?.toLowerCase() === "rejected" && record?.["rejection_reason"] ? (
          <Tooltip title={<span>{record?.["rejection_reason"]}</span>} destroyTooltipOnHide={true}>
            <div>
              <Icon type={"circle_info_simple"} style={{ color: "#D71313" }} />
            </div>
          </Tooltip>
        ) : (
          0
        )
      if (["approved", "auto approved"]?.includes(_?.toLowerCase()) || disabledStatus) {
        return (
          <Badge count={tooltip}>
            <Tag
              bordered={!tagType}
              color={tagType || "default"}
              className="large color-text"
              style={{ margin: 0 }}
            >
              <Flex gap={8} align="center">
                <Text strong={strong}>{statusTextMap[_] || _}</Text>
                {time ? (
                  <>
                    <Text
                      type="secondary"
                      strong={strong}
                      style={{
                        height: "4px",
                        lineHeight: "4px",
                        margin: "0 -4px"
                      }}
                    >
                      •
                    </Text>
                    <Text strong={strong}>{time}</Text>
                  </>
                ) : null}
              </Flex>
            </Tag>
          </Badge>
        )
      } else {
        return (
          <Badge count={tooltip}>
            <Dropdown
              disabled={disabledStatus}
              menu={{
                items: options?.["request_status"]
                  //?.filter(status => _?.toLowerCase() !== status?.toLowerCase())
                  ?.filter((status) =>
                    availableRequestStatusesMap?.[_?.toLowerCase()]
                      ? availableRequestStatusesMap[_?.toLowerCase()]?.includes(
                          status?.toLowerCase()
                        )
                      : true
                  )
                  ?.map((el) => {
                    let statusLowerCase = el?.toLowerCase()
                    let tooltipStatus = alertsTooltipStatusText[statusLowerCase]
                      ? t(alertsTooltipStatusText[statusLowerCase])
                      : null
                    return {
                      key: el,
                      label: (
                        <Tooltip
                          title={tooltipStatus}
                          placement={"right"}
                          destroyTooltipOnHide={true}
                        >
                          <Flex gap={8} align="center">
                            <Badge status={tagMap[statusLowerCase] || "default"} />
                            <Text strong={strong}>{statusTextMap[el] || el}</Text>
                          </Flex>
                        </Tooltip>
                      )
                    }
                  }),
                onClick: ({ key, domEvent }) => {
                  domEvent.stopPropagation()
                  if (key !== _)
                    handleChangeStatus({
                      id: record.id,
                      status_type: "request_status",
                      status_value: key
                    })
                }
              }}
            >
              <Tag
                bordered={!tagType}
                color={tagType || "default"}
                className="large color-text"
                style={{ margin: 0 }}
              >
                <Flex gap={8} align="center">
                  <Text strong={strong}>{statusTextMap[_] || _}</Text>
                  {time ? (
                    <>
                      <Text
                        type="secondary"
                        strong={strong}
                        style={{
                          height: "4px",
                          lineHeight: "4px",
                          margin: "0 -4px"
                        }}
                      >
                        •
                      </Text>
                      <Text strong={strong}>{time}</Text>
                    </>
                  ) : null}
                  <Icon type="chevron_down_small" style={{ marginLeft: -4 }} />
                </Flex>
              </Tag>
            </Dropdown>
          </Badge>
        )
      }
    }
  },
  provider_status: {
    key: "provider_status",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: t("Provider status"),
        sorter: true,
        icon: creditsColumnIconMap["provider_status"],
        stateData: state,
        key: "provider_status"
      }),
    dataIndex: "provider_status",
    minWidth: 180,
    render: (_, record) => {
      if (["approved", "ineligible"]?.includes(_?.toLowerCase()) || disabledStatus) {
        return (
          <Tag
            color={tagMap[_?.toLowerCase()] || "default"}
            key={_}
            className={"simple large"}
            style={{ margin: 0 }}
          >
            <Flex gap={8} align="center">
              <Badge status={tagMap[_?.toLowerCase()] || "default"} color={tagColorMap[_]} />
              <Text strong={strong}>{statusTextMap[_] || _}</Text>
            </Flex>
          </Tag>
        )
      } else {
        return (
          <Dropdown
            disabled={disabledStatus}
            menu={{
              items: options?.["provider_status"]
                ?.filter((status) =>
                  availableProviderStatusesMap?.[_?.toLowerCase()]
                    ? availableProviderStatusesMap[_?.toLowerCase()]?.includes(
                        status?.toLowerCase()
                      )
                    : true
                )
                ?.map((el) => {
                  let statusLowerCase = el?.toLowerCase()
                  let tooltipStatus = alertsTooltipStatusText[statusLowerCase]
                    ? t(alertsTooltipStatusText[statusLowerCase])
                    : null
                  return {
                    key: el,
                    label: (
                      <Tooltip
                        title={tooltipStatus}
                        placement={"right"}
                        destroyTooltipOnHide={true}
                      >
                        <Flex gap={8} align="center">
                          <Badge status={tagMap[statusLowerCase] || "default"} />
                          <Text strong={strong}>{statusTextMap[el] || el}</Text>
                        </Flex>
                      </Tooltip>
                    )
                  }
                }),
              onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation()
                if (key !== _)
                  handleChangeStatus({
                    id: record.id,
                    status_type: "provider_status",
                    status_value: key
                  })
              }
            }}
          >
            <Tag
              color={tagMap[_?.toLowerCase()] || "default"}
              key={_}
              className={"simple large"}
              style={{ margin: 0 }}
            >
              <Flex gap={8} align="center">
                <Badge status={tagMap[_?.toLowerCase()] || "default"} color={tagColorMap[_]} />
                <Text strong={strong}>{statusTextMap[_] || _}</Text>
                <Icon type="chevron_down_small" style={{ marginLeft: -4 }} />
              </Flex>
            </Tag>
          </Dropdown>
        )
      }
    }
  },
  request_type: {
    key: "request_type",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: t("Request type"),
        sorter: true,
        icon: creditsColumnIconMap["request_type"],
        stateData: state,
        key: "request_type"
      }),
    dataIndex: "request_type",
    fixed: isAdmin ? "left" : false,
    width: 180
  },
  alert_status: {
    key: "alert_status",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: t("Alert status"),
        sorter: true,
        icon: alertsColumnIconMap["alert_status"],
        stateData: state,
        key: "alert_status"
      }),
    dataIndex: "alert_status",
    minWidth: 160,
    render: (_, record) => {
      return (
        <Tag
          color={tagMap[_?.toLowerCase()] || "default"}
          key={_}
          className={"simple large"}
          style={{ margin: 0 }}
        >
          <Flex gap={8} align="center">
            <Badge status={tagMap[_?.toLowerCase()] || "default"} color={tagColorMap[_]} />
            <Text strong={strong}>{statusTextMap[_] || _}</Text>
          </Flex>
        </Tag>
      )
    }
  },
  alert_type: {
    key: "alert_type",
    title: (state) =>
      titleSortFilters({
        title: t("Type"),
        icon: alertsColumnIconMap["alert_type"],
        stateData: state,
        key: "alert_type"
      }),
    dataIndex: "alert_type",
    width: 110
  },
  alert_reason: {
    key: "alert_reason",
    title: (state) =>
      titleSortFilters({
        title: t("Alert reason"),
        icon: alertsColumnIconMap["alert_reason"],
        stateData: state,
        key: "alert_reason"
      }),
    dataIndex: "alert_reason",
    ellipsis: true,
    render: (value) => (value ? <Private>{value}</Private> : "—")
    //render: (_, record) => _ ? <a href={record?.['alert_reason_link']} target='_blank'>{_}</a> : '—'
  },
  user_email: {
    key: "user_email",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: t("User email"),
        sorter: true,
        icon: alertsColumnIconMap["user_email"],
        stateData: state,
        key: "user_email"
      }),
    dataIndex: "user_email",
    width: 185,
    render: (value) => (value ? <Private>{value}</Private> : "UNKNOWN")
  },
  transaction_timestamp: {
    key: "transaction_timestamp",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: t("Transaction date"),
        sorter: true,
        icon: alertsColumnIconMap["transaction_timestamp"],
        stateData: state,
        key: "transaction_timestamp"
      }),
    dataIndex: "transaction_timestamp",
    width: 185,
    render: (_) => dayjs(_).format(dateFormat)
  },
  transaction_id: {
    key: "transaction_id",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: t("Transaction ID"),
        sorter: true,
        icon: alertsColumnIconMap["transaction_id"],
        stateData: state,
        key: "transaction_id"
      }),
    dataIndex: "transaction_id",
    width: 185,
    render: (_, record) => {
      if (!_?.length) return "—"
      const visibleIds = _?.slice(0, 2)
      const hasMore = _?.length > 2

      return (
        <Flex gap={4} wrap={true}>
          {visibleIds.map((id, idx) => (
            <a
              key={id}
              href={record?.["transaction_link"]?.[idx] || record?.["transaction_url"]?.[idx]}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              rel="noreferrer"
            >
              <Private>{`${id}${idx < visibleIds.length - 1 ? "," : ""}`}</Private>
            </a>
          ))}
          {hasMore && (
            <Tooltip title={t("View all mapped Transaction IDs in the View Details modal window")}>
              <Flex align="center">
                {t("See more")}
                <Tag bordered={false} color="processing" style={{ marginLeft: 8 }}>
                  {_?.length}
                </Tag>
              </Flex>
            </Tooltip>
          )}
        </Flex>
      )
    }
  },
  alert_timestamp: {
    key: "alert_timestamp",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: t("Alert date"),
        sorter: true,
        icon: alertsColumnIconMap["alert_timestamp"],
        stateData: state,
        key: "alert_timestamp"
      }),
    dataIndex: "alert_timestamp",
    width: 140,
    render: dataCell
  },
  request_date_timestamp: {
    key: "request_date_timestamp",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: t("Request date"),
        sorter: true,
        icon: alertsColumnIconMap["alert_timestamp"],
        stateData: state,
        key: "request_date_timestamp"
      }),
    dataIndex: "request_date_timestamp",
    width: 160,
    render: dataCell
  },
  update_date_timestamp: {
    key: "update_date_timestamp",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: t("Update date"),
        sorter: true,
        icon: alertsColumnIconMap["alert_timestamp"],
        stateData: state,
        key: "update_date_timestamp"
      }),
    dataIndex: "update_date_timestamp",
    width: 160,
    render: dataCell
  },
  alert_service: {
    key: "alert_service",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: t("Alert source"),
        sorter: true,
        icon: alertsColumnIconMap["alert_service"],
        stateData: state,
        key: "alert_service"
      }),
    dataIndex: "alert_service",
    width: 160,
    render: (_) => (
      <Space>
        <img src={Images[serviceImg?.[theme]?.[_]]} alt={_} width={20} height={20} />
        <Text strong={strong}>{serviceMap[_]}</Text>
      </Space>
    )
  },
  amount: {
    key: "amount",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: t("Order value"),
        sorter: true,
        icon: alertsColumnIconMap["amount"],
        stateData: state,
        key: "converted_amount"
      }),
    dataIndex: "converted_amount",
    width: 150,
    render: (_, record) => (
      <Space direction="vertical" size={4}>
        {record?.["converted_currency"] ? (
          <>
            <Text strong={strong}>
              <Private>{`${record?.["converted_currency"]} ${record?.["converted_amount"]}`}</Private>
            </Text>
            <Text type="secondary" strong={strong}>
              <Private>{`${record?.currency || ""} ${record?.amount}`}</Private>
            </Text>
          </>
        ) : (
          <Text strong={strong}>
            <Private>{`${record?.currency || ""} ${record?.amount}`}</Private>
          </Text>
        )}
      </Space>
    )
  },
  request_amount: {
    key: "request_amount",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: t("Alert cost"),
        sorter: true,
        icon: alertsColumnIconMap["amount"],
        stateData: state,
        key: "request_amount"
      }),
    dataIndex: "request_amount",
    width: 180,
    render: (_) => (
      <Text strong={strong}>
        <Private>{`USD ${_}`}</Private>
      </Text>
    )
  },
  merchant_descriptor: {
    key: "merchant_descriptor",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: t("Billing descriptor"),
        sorter: true,
        icon: alertsColumnIconMap["merchant_descriptor"],
        stateData: state,
        key: "merchant_descriptor"
      }),
    dataIndex: "merchant_descriptor",
    width: 185,
    render: (value) => (value ? <Private>{value}</Private> : "—")
  },
  country: {
    key: "country",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: t("Country"),
        sorter: true,
        icon: alertsColumnIconMap["country"],
        stateData: state,
        key: "country"
      }),
    dataIndex: "country",
    width: 185
  },
  bank_name: {
    key: "bank_name",
    sorter: true,
    title: (state) =>
      titleSortFilters({
        title: t("Bank"),
        sorter: true,
        icon: alertsColumnIconMap["bank_name"],
        stateData: state,
        key: "bank_name"
      }),
    dataIndex: "bank_name",
    width: 140,
    render: (value) => (value ? <Private>{value}</Private> : "—")
  },
  card_last4: {
    key: "card_last4",
    title: (state) =>
      titleSortFilters({
        title: t("Card"),
        icon: alertsColumnIconMap["card_last4"],
        stateData: state,
        key: "card_last4"
      }),
    dataIndex: "card_last4",
    render: (_, record) => (
      <Space>
        {record?.brand && (
          <img
            src={Images[`${record?.brand?.toLowerCase()}${theme === "dark" ? "_dark" : ""}`]}
            width={20}
            height={20}
            alt={record?.brand}
          />
        )}
        {_ && (
          <Text strong={strong}>
            <Private mask="••••">{`•••• ${_}`}</Private>
          </Text>
        )}
      </Space>
    ),
    width: 140
  },
  payment_processor: {
    key: "payment_processor",
    title: (state) =>
      titleSortFilters({
        title: t("Payment processor"),
        icon: alertsColumnIconMap["payment_processor"],
        stateData: state,
        key: "payment_processor"
      }),
    dataIndex: "payment_processor",
    width: 185,
    render: (_) => (
      <Space>
        {_ !== "other" && _ !== "-" && (
          <img src={Images[`integration_${_}`]} width={20} height={20} alt={_} />
        )}
        <Text strong={strong}>{paymentProcessorMap[_] || "—"}</Text>
      </Space>
    )
  },
  alert_id: {
    key: "alert_id",
    title: (state) =>
      titleSortFilters({
        title: t("Alert ID"),
        icon: alertsColumnIconMap["alert_id"],
        stateData: state,
        key: "alert_id"
      }),
    dataIndex: "alert_id",
    width: 290,
    render: (_) => {
      if (isAdmin) {
        return (
          <Text
            copyable={{ tooltips: false, text: isIncognitoActive() ? INCOGNITO_MASK : _ }}
            strong={strong}
          >
            <Private>{_}</Private>
          </Text>
        )
      } else {
        return <Private>{_}</Private>
      }
    }
    //fixed: 'right',
  },
  rejection_reason: {
    key: "rejection_reason",
    title: (state) =>
      titleSortFilters({
        title: t("Rejection reason"),
        icon: alertsColumnIconMap["rejection_reason"],
        stateData: state,
        key: "rejection_reason"
      }),
    dataIndex: "rejection_reason",
    width: 185,
    render: (_, record) => {
      const details = record?.["rejection_reason_details"]
      return details ? `${_} (${details})` : _
    }
  },
  original_alert_id: {
    key: "original_alert_id",
    title: (state) =>
      titleSortFilters({
        title: t("Original alert"),
        icon: alertsColumnIconMap["alert_id"],
        stateData: state,
        key: "original_alert_id"
      }),
    dataIndex: "original_alert_id",
    minWidth: 150,
    render: (value) => {
      if (!value) return "—"
      return (
        <Text copyable={{ tooltips: false }} strong={strong}>
          {value}
        </Text>
      )
    }
  }
})

export const getDefaultColumn = ({ field_id, label, t, icon }) => ({
  key: field_id,
  sorter: !["provider_reason", "attachments", "request_reason", "rejection_reason"].includes(
    field_id
  ),
  title: (state) =>
    titleSortFilters({
      title: t(label),
      sorter: !["provider_reason", "attachments", "request_reason", "rejection_reason"].includes(
        field_id
      ),
      icon: icon || creditsColumnIconMap[field_id],
      stateData: state,
      key: field_id
    }),
  dataIndex: field_id,
  ellipsis: true,
  width: 185,
  render: (_) => {
    if (field_id === "attachments") return _?.length
    if (["arn", "auth_code"].includes(field_id)) return _ ? <Private>{_}</Private> : "—"
    if (field_id === "request_reason" && _?.toLowerCase() === "other") return "Other"
    return _ || "—"
  }
})

export const getActionColumn = ({
  onSelectRow,
  isAdmin,
  disabled,
  isDemo = false,
  isBusinessAccountAdministrator = false
}) => {
  const handleAction = (e, data, type) => {
    e.preventDefault()
    e.stopPropagation()
    onSelectRow(data, type)
  }

  if (isAdmin) {
    return [
      {
        key: "action_block",
        className: "actions-small-padding-right",
        title: "",
        width: 88,
        render: (_, record) => (
          <Flex gap={4}>
            <Tooltip
              title={record?.["admin_screenshots"]?.length ? "Edit comments" : "Add comments"}
            >
              <ButtonComponent
                type="link"
                shape="circle"
                icon={
                  <Icon type={record?.["admin_screenshots"]?.length ? "edit_small" : "camera"} />
                }
                onClick={(e) => handleAction(e, record, "add_comment")}
                className="flex center"
                disabled={disabled}
              />
            </Tooltip>

            <Tooltip title={record?.["is_archived"] ? "Unarchive" : "Archive"}>
              <ButtonComponent
                type="link"
                shape="circle"
                icon={<Icon type={record?.["is_archived"] ? "unarchive" : "archive"} />}
                onClick={(e) => handleAction(e, record, "archive")}
                className="flex center"
                disabled={disabled}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <ButtonComponent
                danger
                type="link"
                shape="circle"
                icon={<Icon type="delete" />}
                onClick={(e) => handleAction(e, record, "delete")}
                className="flex center table-action-btn"
                disabled={disabled}
              />
            </Tooltip>
          </Flex>
        )
      },
      {
        key: "action",
        className: "actions-small-padding-left",
        title: "",
        width: 64,
        fixed: "right",
        render: (_, record) => (
          <Tooltip title="View Details">
            <ButtonComponent
              type="link"
              shape="circle"
              icon={<Icon type={"layout_align_right"} />}
              onClick={(e) => handleAction(e, record, "view_details")}
              className="flex center"
              disabled={disabled}
            />
          </Tooltip>
        )
      }
    ]
  }

  const isActionDisabled = isBusinessAccountAdministrator ? false : isDemo

  return [
    {
      key: "action",
      title: "",
      width: 64,
      fixed: "right",
      render: (_, record) => (
        <Flex gap={4}>
          {record?.request_status !== "Requires proof" && (
            <Tooltip title="Preview credit request">
              <ButtonComponent
                type="link"
                shape="circle"
                icon={<Icon type={"eye"} />}
                onClick={(e) => handleAction(e, record, "preview")}
                className="flex center"
                disabled={isDemo || isActionDisabled}
              />
            </Tooltip>
          )}

          {record?.request_status === "Requires proof" && (
            <Tooltip title="Add evidences">
              <ButtonComponent
                type="link"
                shape="circle"
                icon={<Icon type={"shot"} />}
                onClick={(e) => handleAction(e, record, "attach")}
                className="flex center"
                disabled={isDemo || isActionDisabled}
              />
            </Tooltip>
          )}
        </Flex>
      )
    }
  ]
}
